import {
  MobileTab,
  useMobileNavigation,
} from '@/contexts/MobileNavigationContext/MobileNavigationContext';
import {useUnreadNotificationsCountQuery} from '@/generated/graphql';
import {useSubscribeToNotifications} from '@/hooks/useInitializeNotifications';
import {tokens} from '@ark/web';
import {
  ApartmentRounded,
  Chat,
  Dashboard,
  MoreHoriz,
  Notifications,
} from '@mui/icons-material';
import {Badge, Box, Typography} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {ReactNode} from 'react';
import {useNavigate} from 'react-router-dom';

const TAB_BAR_HEIGHT = 64;

export {TAB_BAR_HEIGHT};

type TabConfig = {
  id: MobileTab;
  label: string;
  icon: ReactNode;
  path?: string;
  dataAttr: string;
};

const TABS: TabConfig[] = [
  {
    id: 'home',
    label: 'Home',
    icon: <Dashboard />,
    path: '/dashboard',
    dataAttr: 'mobile-bottom-tabs-home-open',
  },
  {
    id: 'communities',
    label: 'Communities',
    icon: <ApartmentRounded />,
    path: '/communities',
    dataAttr: 'mobile-bottom-tabs-communities-open',
  },
  {
    id: 'messages',
    label: 'Messages',
    icon: <Chat />,
    path: '/messages',
    dataAttr: 'mobile-bottom-tabs-messages-open',
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: <Notifications />,
    path: '/notifications',
    dataAttr: 'mobile-bottom-tabs-notifications-open',
  },
  {
    id: 'more',
    label: 'More',
    icon: <MoreHoriz />,
    dataAttr: 'mobile-bottom-tabs-more-open',
  },
];

export const MobileBottomTabs = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {activeTab, openMoreSheet} = useMobileNavigation();
  const {data, refetch} = useUnreadNotificationsCountQuery();

  useSubscribeToNotifications(() => {
    refetch();
  });

  const unreadCount = data?.notifications?.totalCount ?? 0;
  const activeColor = theme.palette.primary.faded ?? '#15659f';

  const handleTabPress = (tab: TabConfig) => {
    if (tab.id === 'more') {
      openMoreSheet();
    } else if (tab.path) {
      navigate(tab.path);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        minHeight: `${TAB_BAR_HEIGHT}px`,
        height: `calc(${TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))`,
        paddingBottom: 'env(safe-area-inset-bottom)',
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'space-around',
        zIndex: 1200,
      }}
    >
      {TABS.map(tab => {
        const isActive = activeTab === tab.id;
        const isNotification = tab.id === 'notifications';

        return (
          <Box
            key={tab.id}
            onClick={() => handleTabPress(tab)}
            data-attr={tab.dataAttr}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              position: 'relative',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              transition: `color ${tokens.transition.fast}, background-color ${tokens.transition.normal}`,
              color: isActive ? activeColor : theme.palette.text.secondary,
              backgroundColor: isActive ? `${activeColor}0D` : 'transparent',
              '&:active': {
                transform: 'scale(0.95)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                backgroundColor: isActive ? activeColor : 'transparent',
                transition: `background-color ${tokens.transition.normal}`,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 0.25,
                '& svg': {
                  fontSize: 22,
                },
              }}
            >
              {isNotification && unreadCount > 0 ? (
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: 10,
                      height: 16,
                      minWidth: 16,
                    },
                  }}
                >
                  {tab.icon}
                </Badge>
              ) : (
                tab.icon
              )}
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: isActive ? 600 : 400,
                lineHeight: 1,
                letterSpacing: '0.02em',
              }}
            >
              {tab.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
